import React, { FC, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Radio, RadioChangeEvent, Space } from 'antd';

import type { CellFillSettings } from '../../../../../../helpers/utils/tableHelpers';
import { showErrorMessage } from '../../../../../../helpers/utils/ui';
import FillingConditions from '../../create-mertrics/FillingConditions';

/**
 * Заливка ячеек обычной (не кастомной) метрики по условиям «от – до» — «…» столбца → «Заливка ячеек».
 * Те же условия и режимы, что в редакторе кастомной метрики (EditMetricsModal), но хранятся в отчёте
 * (table.cellFills), а не в метрике: у стандартных метрик своих настроек на сервере нет.
 */

interface ConditionRow {
    id: number;
    from: string;
    to: string;
    value: string;
}

interface CellFillModalProps {
    visible: boolean;
    /** Название метрики — в заголовке окна */
    metricName: string;
    /** Текущие настройки столбца; undefined — заливки ещё нет */
    value?: CellFillSettings;
    /** null — заливку столбца убрать */
    onSave: (settings: CellFillSettings | null) => void;
    onCancel: () => void;
}

// Как в редакторе кастомной метрики
const MAX_CONDITIONS = 10;
// Светлый цвет по умолчанию: на чёрном (как в редакторе метрики) число в ячейке не прочитать
const DEFAULT_COLOR = '#d9f7be';

const isBlank = (value: unknown) => value === '' || value === null || value === undefined;

const toRows = (value?: CellFillSettings): ConditionRow[] => {
    const source = value?.fillingConditions?.length
        ? value.fillingConditions
        : [ { from: '', to: '', value: DEFAULT_COLOR } ];
    return source.map((condition, index) => ({
        id: index + 1,
        from: condition.from as string,
        to: condition.to as string,
        value: condition.value || DEFAULT_COLOR,
    }));
};

const CellFillModal: FC<CellFillModalProps> = ({ visible, metricName, value, onSave, onCancel }) => {
    const [ isFilling, setIsFilling ] = useState<boolean>(value?.isFilling ?? false);
    const [ rows, setRows ] = useState<ConditionRow[]>(() => toRows(value));

    const addCondition = () => {
        if (rows.length >= MAX_CONDITIONS) {
            showErrorMessage(`Достигнут лимит по условиям ${MAX_CONDITIONS} штук.`);
            return;
        }
        // id — больше всех прежних: после удаления строк length + 1 совпал бы с существующим
        const nextId = rows.reduce((max, row) => Math.max(max, row.id), 0) + 1;
        setRows((prev) => [ ...prev, { id: nextId, from: '', to: '', value: DEFAULT_COLOR } ]);
    };

    const handleChange = (index: number, field: string, newValue: string) => {
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: newValue } : row)));
    };

    const handleClear = (index: number) => {
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, from: '', to: '', value: DEFAULT_COLOR } : row)));
    };

    const handleDelete = (id: number) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
    };

    const handleSave = () => {
        // Пустые «от» / «до» — без границы (getCellFillStyle): условие без диапазона закрашивает все ячейки.
        // Поэтому строки не выбрасываем — иначе «Заливать всю ячейку» + цвет без чисел молча убирал заливку
        const withColor = rows.filter((row) => Boolean(row.value));
        if (withColor.length === 0) {
            onSave(null);
            return;
        }
        if (withColor.some((row) => !isBlank(row.from) && !isBlank(row.to) && Number(row.from) > Number(row.to))) {
            showErrorMessage('В условии «от» больше, чем «до» — поправьте диапазон');
            return;
        }
        onSave({
            isFilling,
            fillingConditions: withColor.map(({ from, to, value: color }) => ({
                from: isBlank(from) ? '' : from,
                to: isBlank(to) ? '' : to,
                value: color,
            })),
        });
    };

    return (
        <Modal
            open={visible}
            title={`Заливка ячеек: ${metricName}`}
            onCancel={onCancel}
            width={620}
            destroyOnClose
            footer={[
                value && (
                    <Button key="reset" danger onClick={() => onSave(null)}>
                        Убрать заливку
                    </Button>
                ),
                <Button key="cancel" onClick={onCancel}>
                    Отмена
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    Сохранить
                </Button>,
            ]}
        >
            <div style={{ display: 'flex', rowGap: '10px', flexDirection: 'column' }}>
                <span style={{ color: '#8c8c8c' }}>
                    Значение ячейки попало в диапазон «от – до» — ячейка закрашивается цветом условия.
                    Пустое «от» или «до» — без границы: оставьте оба пустыми, чтобы закрасить весь столбец.
                    Подходят несколько условий — берётся нижнее. Настройка сохраняется в отчёте.
                </span>
                <span style={{ fontWeight: '700', marginTop: '8px' }}>Условия заливки ячейки:</span>
                <Radio.Group
                    value={isFilling ? 'valueBased' : 'full'}
                    onChange={(e: RadioChangeEvent) => setIsFilling(e.target.value === 'valueBased')}
                >
                    <Space direction="vertical">
                        <Radio value="full">Заливать всю ячейку</Radio>
                        <Radio value="valueBased">Заливать ячейку от значения</Radio>
                    </Space>
                </Radio.Group>
                {rows.map((row, index) => (
                    <FillingConditions
                        key={row.id}
                        id={index === 0 ? 1 : row.id + 1}
                        from={row.from}
                        to={row.to}
                        value={row.value}
                        onClear={() => handleClear(index)}
                        onDelete={() => handleDelete(row.id)}
                        onChange={(field, newValue) => handleChange(index, field, newValue)}
                    />
                ))}
            </div>
            <Space style={{ marginTop: '16px' }}>
                <span style={{ fontWeight: '600' }}>Добавить условие заливки</span>
                <Button onClick={addCondition} icon={<PlusOutlined />} type="primary" shape="circle" />
            </Space>
        </Modal>
    );
};

export default CellFillModal;
